// Share links, stage 1 of design/sharing.md: the script and its scene, as deflated JSON in the
// URL fragment. The fragment never reaches the server, so there's nothing to store or moderate.
// Links are #z=<base32768>, which packs 15 bits into each character, or the plain ASCII
// #s=<base64url> for places that mangle Unicode.
import { decode as fromBase32768, encode as toBase32768 } from './vendor/base32768/index.js';

export interface Share {
  /** Share format version. */
  v: 1;
  /** Simulation version of the VM that made it. */
  sim: number;
  src: string;
  enemy?: string;
  move?: string;
  rank?: number;
  seed?: number;
  vars?: Record<string, number>;
  /** Beat clock: [bpm, offset in ticks]. Absent means off. */
  beat?: [number, number];
  /** 0 easy to 3 lunatic. Absent means normal. */
  difficulty?: number;
}

/** Values a share leaves out, because they're what the testbed starts with anyway. */
export const SHARE_DEFAULTS = { enemy: 'boss', move: 'static', rank: 0.5, seed: 1 };

/** Past this many characters chat apps start cutting links short. */
export const LONG_LINK = 4000;

/** `z` is the short Unicode form, `s` the ASCII one. */
export type LinkForm = 'z' | 's';

/** Whether a location hash is a share link of either form. */
export function isShareHash(hash: string): boolean {
  return /^#?[zs]=/.test(hash);
}

const VAR_NAME = /^var[1-8]$/;

/** Builds the share, leaving out every field that's at its default. */
export function makeShare(scene: {
  sim: number;
  src: string;
  enemy: string;
  move: string;
  rank: number;
  seed: number;
  vars: Map<string, number>;
  beat: [number, number] | null;
  difficulty: number | null;
}): Share {
  const share: Share = { v: 1, sim: scene.sim, src: scene.src };
  if (scene.enemy !== SHARE_DEFAULTS.enemy) share.enemy = scene.enemy;
  if (scene.move !== SHARE_DEFAULTS.move) share.move = scene.move;
  if (scene.rank !== SHARE_DEFAULTS.rank) share.rank = scene.rank;
  if (scene.seed !== SHARE_DEFAULTS.seed) share.seed = scene.seed;
  if (scene.vars.size > 0) share.vars = Object.fromEntries(scene.vars);
  if (scene.beat) share.beat = scene.beat;
  if (scene.difficulty !== null) share.difficulty = scene.difficulty;
  return share;
}

export async function shareLink(share: Share, form: LinkForm): Promise<string> {
  const bytes = await pipe(new TextEncoder().encode(JSON.stringify(share)), new CompressionStream('deflate-raw'));
  const text = form === 'z' ? toBase32768(bytes) : toBase64Url(bytes);
  return `${location.origin}/#${form}=${text}`;
}

/**
 * Reads a share from a location hash (`#z=...` or `#s=...`). A link copied out of an address bar
 * may come back percent-encoded, so that's undone first. Throws with a readable message if it
 * isn't a share.
 */
export async function readShare(hash: string): Promise<Share> {
  const m = /^#?([zs])=(.*)$/s.exec(hash);
  if (!m) throw new Error("the link isn't a bs share");
  let payload = m[2];
  try {
    payload = decodeURIComponent(payload);
  } catch {
    // Not percent-encoded after all; use it as it is.
  }
  let json: unknown;
  try {
    const packed = m[1] === 'z' ? fromBase32768(payload) : fromBase64Url(payload);
    const bytes = await pipe(packed, new DecompressionStream('deflate-raw'));
    json = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new Error("the link is cut short or isn't a bs share");
  }
  return checkShare(json);
}

function checkShare(json: unknown): Share {
  if (typeof json !== 'object' || json === null) throw new Error("the link isn't a bs share");
  const s = json as Record<string, unknown>;
  if (s.v !== 1) throw new Error(`share format ${String(s.v)} is newer than this testbed understands`);
  if (typeof s.src !== 'string') throw new Error('the share has no script');
  if (typeof s.sim !== 'number') throw new Error('the share has no simulation version');
  const share: Share = { v: 1, sim: s.sim, src: s.src };
  if (typeof s.enemy === 'string') share.enemy = s.enemy;
  if (typeof s.move === 'string') share.move = s.move;
  if (isNumber(s.rank)) share.rank = s.rank;
  if (isNumber(s.seed)) share.seed = s.seed;
  if (typeof s.vars === 'object' && s.vars !== null) {
    const vars = Object.entries(s.vars).filter(([k, v]) => VAR_NAME.test(k) && isNumber(v));
    if (vars.length > 0) share.vars = Object.fromEntries(vars) as Record<string, number>;
  }
  if (Array.isArray(s.beat) && s.beat.length === 2 && s.beat.every(isNumber) && s.beat[0] > 0) {
    share.beat = [s.beat[0], s.beat[1]];
  }
  if (isNumber(s.difficulty) && s.difficulty >= 0 && s.difficulty <= 3) share.difficulty = s.difficulty;
  return share;
}

function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

async function pipe(bytes: Uint8Array, transform: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(transform);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}
